import { useState, type ReactNode } from 'react';
import { input as inputCls, sectionTitle } from '../lib/ui.ts';
import type { Build } from '../lib/types.ts';

/**
 * Builds table that shows the latest ~10 by default and collapses the full,
 * searchable list into an expander. Search matches a per-row haystack
 * (client / slug / date / outcome). Each dashboard passes its own column header
 * and row renderer so the columns stay table-specific.
 */
export function BuildsTableSection({
  title,
  builds,
  header,
  renderRow,
  haystack,
  emptyText = 'No builds yet.',
  limit = 10,
  collapsed = false,
}: {
  title: string;
  builds: Build[];
  header: ReactNode;
  renderRow: (b: Build) => ReactNode;
  haystack: (b: Build) => string;
  emptyText?: string;
  limit?: number;
  /**
   * De-emphasize the whole section behind a closed expander (M4b #3). For the
   * DESIGNER flow this is BI, not their preview — the build chat's section rail is
   * the authoritative preview, so the table shouldn't compete for attention.
   */
  collapsed?: boolean;
}) {
  const [query, setQuery] = useState('');
  const q = query.trim().toLowerCase();
  const filtered = q ? builds.filter((b) => haystack(b).toLowerCase().includes(q)) : builds;
  const latest = builds.slice(0, limit);

  if (collapsed) {
    return (
      <details className="rounded border bg-paper p-3">
        <summary className="cursor-pointer text-sm font-medium text-ink-2">
          {title} <span className="text-ink-2">({builds.length}) — build history / BI</span>
        </summary>
        <div className="mt-2">
          <Table header={header} rows={latest} renderRow={renderRow} emptyText={emptyText} />
          {builds.length > limit && (
            <p className="mt-1 text-xs text-ink-2">Showing the latest {limit} of {builds.length}.</p>
          )}
        </div>
      </details>
    );
  }

  return (
    <section>
      <h2 className={`mb-2 ${sectionTitle}`}>{title}</h2>
      <Table header={header} rows={latest} renderRow={renderRow} emptyText={emptyText} />
      {builds.length > limit && (
        <details className="mt-2 rounded border bg-paper p-3">
          <summary className="cursor-pointer text-sm font-medium text-ink">
            Search all builds ({builds.length})
          </summary>
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="search client / slug / date / outcome"
            className={`${inputCls} my-2 w-full`}
          />
          <Table header={header} rows={filtered} renderRow={renderRow} emptyText="No matches." />
          <p className="mt-1 text-xs text-ink-2">
            {filtered.length} of {builds.length}
          </p>
        </details>
      )}
    </section>
  );
}

function Table({
  header,
  rows,
  renderRow,
  emptyText,
}: {
  header: ReactNode;
  rows: Build[];
  renderRow: (b: Build) => ReactNode;
  emptyText: string;
}) {
  return (
    <div className="overflow-x-auto rounded border bg-paper">
      <table className="w-full text-left text-sm">
        <thead className="border-b bg-paper-2 text-xs uppercase text-ink-2">{header}</thead>
        <tbody>
          {rows.map(renderRow)}
          {rows.length === 0 && (
            <tr>
              <td colSpan={12} className="px-3 py-2 text-ink-2">
                {emptyText}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
